import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GitCompare, X, ShoppingBag } from "lucide-react";
import { useCompare } from "@/contexts/CompareContext";
import { externalLinkProps } from "@/lib/externalLink";

/** Sticky bottom tray + side-by-side drawer that shows when 1+ products are added to compare. */
export const CompareTray = () => {
  const { items, remove, clear } = useCompare();
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  return (
    <>
      <div
        data-testid="compare-tray"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 glass rounded-full pl-2 pr-1.5 py-1.5 flex items-center gap-2 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.6)]"
      >
        <div className="flex -space-x-2 mr-2">
          {items.map((p) => (
            <div
              key={p.id}
              className="w-9 h-9 rounded-full overflow-hidden border-2 border-card bg-secondary"
              title={p.strain_name}
            >
              <img
                src={p.image_url}
                alt={p.strain_name}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        <span className="text-xs text-foreground/80 pr-2 hidden sm:inline">
          {items.length} to compare
        </span>
        <Button
          data-testid="compare-open"
          onClick={() => setOpen(true)}
          className="rounded-full h-10 bg-primary text-primary-foreground hover:bg-primary/90 px-5"
        >
          <GitCompare className="w-4 h-4 mr-2" /> Compare
        </Button>
        <button
          data-testid="compare-clear"
          onClick={clear}
          className="w-9 h-9 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground inline-flex items-center justify-center"
          aria-label="Clear compare"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl rounded-2xl p-0 overflow-hidden"
          data-testid="compare-dialog"
        >
          <div className="px-6 pt-6 pb-4 border-b border-border">
            <DialogTitle className="font-serif text-2xl">
              Side-by-side
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Pick the right shop in one glance.
            </DialogDescription>
          </div>
          <div className="p-6 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <tbody>
                <Row label="" cells={items.map((p) => (
                  <div key={p.id} className="text-center">
                    <div className="w-full aspect-square rounded-2xl overflow-hidden bg-secondary mb-3 relative">
                      <img
                        src={p.image_url}
                        alt={p.strain_name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        data-testid={`compare-remove-${p.id}`}
                        onClick={() => remove(p.id)}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-card/90 text-foreground inline-flex items-center justify-center hover:bg-card"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))} />
                <Row label="Strain" cells={items.map((p) => (
                  <div key={p.id} className="text-center font-serif text-lg">
                    {p.strain_name}
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                      {p.strain_type} · {p.brand}
                    </div>
                  </div>
                ))} />
                <Row label="Dispensary" cells={items.map((p) => (
                  <div key={p.id} className="text-center">
                    <div className="text-sm">{p.dispensary_name}</div>
                    {p.distance_mi != null && (
                      <div className="text-xs text-muted-foreground">
                        {p.distance_mi} mi
                      </div>
                    )}
                  </div>
                ))} />
                <Row label="Price" cells={items.map((p) => {
                  const cheapest =
                    Math.min(...items.map((x) => x.price)) === p.price;
                  return (
                    <div key={p.id} className="text-center">
                      <div
                        className={`price-tag text-2xl ${cheapest ? "ring-2 ring-emerald-500/40 rounded-lg px-2 py-1 inline-block" : ""}`}
                      >
                        ${p.price.toFixed(2)}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
                        {p.unit}
                      </div>
                      {cheapest && items.length > 1 && (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mt-1">
                          Cheapest
                        </div>
                      )}
                    </div>
                  );
                })} />
                <Row label="THC" cells={items.map((p) => {
                  const highest = Math.max(...items.map((x) => x.thc_pct)) === p.thc_pct;
                  return (
                    <div
                      key={p.id}
                      className={`text-center ${highest && items.length > 1 ? "text-accent" : ""}`}
                      style={highest && items.length > 1 ? { color: "#D4A268" } : {}}
                    >
                      {p.thc_pct}%
                    </div>
                  );
                })} />
                <Row label="CBD" cells={items.map((p) => (
                  <div key={p.id} className="text-center">{p.cbd_pct}%</div>
                ))} />
                <Row label="Stock" cells={items.map((p) => (
                  <div key={p.id} className="text-center text-xs">
                    {p.stock_status === "in_stock" ? (
                      <span className="text-emerald-400">In stock</span>
                    ) : p.stock_status === "low_stock" ? (
                      <span className="text-amber-300">Low stock</span>
                    ) : (
                      <span className="text-rose-400">Sold out</span>
                    )}
                  </div>
                ))} />
                <Row label="Pickup / Delivery" cells={items.map((p) => (
                  <div key={p.id} className="text-center text-xs space-x-2">
                    {p.dispensary_pickup && <span>Pickup</span>}
                    {p.dispensary_delivery && <span>Delivery</span>}
                  </div>
                ))} />
                <Row label="" cells={items.map((p) => (
                  <div key={p.id} className="text-center">
                    <Button
                      asChild
                      disabled={p.stock_status === "out_of_stock"}
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-5"
                    >
                      <a
                        data-testid={`compare-order-${p.id}`}
                        {...externalLinkProps(p.order_url)}
                      >
                        <ShoppingBag className="w-3.5 h-3.5 mr-2" />
                        Order Now
                      </a>
                    </Button>
                  </div>
                ))} />
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Row = ({ label, cells }) => (
  <tr className="border-b border-border last:border-none">
    <td className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground py-3 pr-4 w-32 align-middle">
      {label}
    </td>
    {cells.map((c, i) => (
      <td key={i} className="py-3 px-2 align-middle">{c}</td>
    ))}
  </tr>
);
